import { env } from '@fjuel/configs/env';
import {
  ApplicationInsights,
  DistributedTracingModes,
  type IExceptionTelemetry,
} from '@microsoft/applicationinsights-web';

let applicationInsights: ApplicationInsights | undefined;

const correlationHeaderDomains = [
  env.VITE_API_URL,
  env.VITE_PORT_MANAGEMENT_API_URL,
  env.VITE_TENANT_MANAGEMENT_API_URL,
  env.VITE_B2B_API_BASE_URL,
]
  .filter((apiUrl): apiUrl is string => apiUrl !== undefined)
  .map((apiUrl) => `^${new URL(apiUrl).host}$`);

export const initializeApplicationInsights = (): ApplicationInsights | undefined => {
  if (applicationInsights || env.DEV || !env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING) {
    return applicationInsights;
  }

  applicationInsights = new ApplicationInsights({
    config: {
      connectionString: env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      enableUnhandledPromiseRejectionTracking: true,
      distributedTracingMode: DistributedTracingModes.W3C,
      correlationHeaderDomains,
    },
  });
  applicationInsights.loadAppInsights();

  return applicationInsights;
};

const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === 'string' ? error : 'Unknown application error');
};

export const trackException = (error: unknown, properties?: Record<string, string>): void => {
  const telemetry = initializeApplicationInsights();
  if (!telemetry) {
    return;
  }

  const exception: IExceptionTelemetry = {
    exception: toError(error),
    properties,
  };
  telemetry.trackException(exception);
};

export const trackEvent = (name: string, properties?: Record<string, string>): void => {
  const telemetry = initializeApplicationInsights();
  if (!telemetry) {
    return;
  }

  telemetry.trackEvent({ name }, properties);
};
